import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Field,
  FieldLabel,
  FieldValidation,
  Icon,
  Input,
} from '@glade-ai/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useCallback, useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

import { PAYMENT_GATE_TYPES, type PaymentGateConfig } from '@/typings/api-models';

import { type PaymentGateFormValues, paymentGateConfigSchema } from './paymentGateConfigSchema';

/** A workflow invoice shown in the invoice selector. */
export type WorkflowInvoiceOption = {
  /** WorkflowInvoice.id — used as `invoiceTemplateRef` on the gate config. */
  id: string;
  /** Human-readable title. */
  label: string;
};

export type WorkflowContextOption = {
  slug: string;
  label: string;
};

export type ServerFieldError = {
  gateIndex: number;
  path: string;
  message: string;
};

export type PaymentGateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialGates?: PaymentGateConfig[];
  workflowInvoices: WorkflowInvoiceOption[];
  workflowContexts: WorkflowContextOption[];
  onSave: (
    values: PaymentGateConfig[],
    helpers: { setErrorsFromServer: (errors: ServerFieldError[]) => void },
  ) => Promise<void> | void;
  isSaving?: boolean;
};

const EMPTY_FORM_VALUES: PaymentGateFormValues = {
  invoiceTemplateRef: '',
  gateType: PAYMENT_GATE_TYPES.MINIMUM,
  minimumAmount: null,
  minimumPercentage: null,
};

/** Convert an existing config into RHF-friendly form values (null pass-throughs). */
const toFormValues = (gate: PaymentGateConfig): PaymentGateFormValues => ({
  invoiceTemplateRef: gate.invoiceTemplateRef,
  gateType: gate.gateType,
  minimumAmount: typeof gate.minimumAmount === 'number' ? gate.minimumAmount : null,
  minimumPercentage: typeof gate.minimumPercentage === 'number' ? gate.minimumPercentage : null,
});

/** Normalize validated form values to the API-shaped `PaymentGateConfig`. */
const toApiGate = (formValues: PaymentGateFormValues): PaymentGateConfig => ({
  invoiceTemplateRef: formValues.invoiceTemplateRef,
  gateType: formValues.gateType,
  ...(formValues.gateType === PAYMENT_GATE_TYPES.MINIMUM && typeof formValues.minimumAmount === 'number'
    ? { minimumAmount: formValues.minimumAmount }
    : {}),
  ...(formValues.gateType === PAYMENT_GATE_TYPES.PERCENTAGE && typeof formValues.minimumPercentage === 'number'
    ? { minimumPercentage: formValues.minimumPercentage }
    : {}),
});

/**
 * Payment gate modal — invoice + gate type + amount/percentage.
 * Uses the `@glade-ai/ui` Dialog; host pane stacking is handled the same way
 * as `PreFilingWarningsDialog` — bump both DialogContent and its overlay to
 * `z-[10001]` until `@glade-ai/ui` exposes an `overlayClassName` prop.
 */
export const PaymentGateModal: FC<PaymentGateModalProps> = ({
  isOpen,
  onClose,
  initialGates,
  workflowInvoices,
  onSave,
  isSaving = false,
}) => {
  const initialGate = initialGates?.[0];
  const formMethods = useForm<PaymentGateFormValues>({
    resolver: zodResolver(paymentGateConfigSchema),
    defaultValues: initialGate ? toFormValues(initialGate) : { ...EMPTY_FORM_VALUES },
    mode: 'onSubmit',
  });
  const { control, handleSubmit, setError, reset } = formMethods;
  const gateType = useWatch({ control, name: 'gateType' });

  // `useForm.defaultValues` only apply at mount. When this modal stays mounted
  // and reopens with different `initialGates` (e.g. switching from create to
  // edit mode on CORE-1318's chip-click path), the form would otherwise keep
  // stale state — reset on each open transition.
  useEffect(() => {
    if (!isOpen) return;
    reset(initialGate ? toFormValues(initialGate) : { ...EMPTY_FORM_VALUES });
    // initialGate is derived from initialGates; depending on both would double-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialGates, reset]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) onClose();
    },
    [onClose],
  );

  const setErrorsFromServer = useCallback(
    (errors: ServerFieldError[]) => {
      for (const error of errors) {
        // RHF's `name` is a discriminated union over the exact leaf paths of
        // the form shape; a dynamic server-supplied path can't be expressed
        // there, so cast via unknown to satisfy the setError signature.
        const fieldName = error.path as unknown as Parameters<typeof setError>[0];
        setError(fieldName, { message: error.message, type: 'server' });
      }
    },
    [setError],
  );

  const onSubmit = handleSubmit(async (formValues) => {
    await onSave([toApiGate(formValues)], { setErrorsFromServer });
  });

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {/* TODO(marcus): Replace this style tag with `overlayClassName="z-[10001]"` on
          DialogContent once @glade-ai/ui exposes it. Mirrors PreFilingWarningsDialog. */}
      <style>{`[data-slot="dialog-overlay"]:has(~ .payment-gate-modal) { z-index: 10001 !important; }`}</style>
      <DialogContent className='payment-gate-modal z-[10001] sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Payment Gate</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            void onSubmit().catch(() => {});
          }}
          className='flex flex-col gap-4'
        >
          <Controller
            control={control}
            name='invoiceTemplateRef'
            render={({ field, fieldState }) => {
              const selectedInvoice = workflowInvoices.find((inv) => inv.id === field.value);
              const noInvoices = workflowInvoices.length === 0;
              const triggerLabel = noInvoices
                ? 'No invoices attached to this workflow'
                : selectedInvoice
                  ? selectedInvoice.label
                  : 'Select an invoice attached to this workflow…';

              return (
                <Field>
                  <FieldLabel>Invoice</FieldLabel>
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type='button'
                        variant='outline'
                        disabled={noInvoices}
                        className='w-full justify-between font-normal'
                        aria-label='Invoice'
                      >
                        <span className='truncate text-left'>{triggerLabel}</span>
                        <Icon icon='ph--caret-down--regular' size={4} className='shrink-0 opacity-50' aria-hidden />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align='start'
                      className='z-[10002] max-h-60 min-w-[var(--radix-dropdown-menu-trigger-width)]'
                    >
                      <DropdownMenuRadioGroup value={field.value || undefined} onValueChange={field.onChange}>
                        {workflowInvoices.map((invoice) => (
                          <DropdownMenuRadioItem key={invoice.id} value={invoice.id}>
                            {invoice.label}
                          </DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {fieldState.error?.message && <FieldValidation>{fieldState.error.message}</FieldValidation>}
                </Field>
              );
            }}
          />

          <Controller
            control={control}
            name='gateType'
            render={({ field }) => (
              <Field>
                <FieldLabel>Gate type</FieldLabel>
                <div role='radiogroup' aria-label='Gate type' className='flex gap-2'>
                  <Button
                    type='button'
                    role='radio'
                    size='sm'
                    variant={field.value === PAYMENT_GATE_TYPES.MINIMUM ? 'default' : 'outline'}
                    aria-checked={field.value === PAYMENT_GATE_TYPES.MINIMUM}
                    onClick={() => field.onChange(PAYMENT_GATE_TYPES.MINIMUM)}
                  >
                    Minimum amount
                  </Button>
                  <Button
                    type='button'
                    role='radio'
                    size='sm'
                    variant={field.value === PAYMENT_GATE_TYPES.PERCENTAGE ? 'default' : 'outline'}
                    aria-checked={field.value === PAYMENT_GATE_TYPES.PERCENTAGE}
                    onClick={() => field.onChange(PAYMENT_GATE_TYPES.PERCENTAGE)}
                  >
                    Percentage of invoice
                  </Button>
                </div>
              </Field>
            )}
          />

          {gateType === PAYMENT_GATE_TYPES.MINIMUM && (
            <Controller
              control={control}
              name='minimumAmount'
              render={({ field, fieldState }) => (
                <Field>
                  <FieldLabel>Minimum amount (USD)</FieldLabel>
                  <Input
                    type='number'
                    min={1}
                    inputMode='numeric'
                    value={typeof field.value === 'number' ? String(Math.round(field.value / 100)) : ''}
                    onChange={(event) => {
                      const next = event.target.value;
                      if (next === '') {
                        field.onChange(null);
                        return;
                      }
                      const cents = Math.round(Number(next) * 100);
                      field.onChange(Number.isFinite(cents) ? cents : null);
                    }}
                    placeholder='250'
                  />
                  {fieldState.error?.message && <FieldValidation>{fieldState.error.message}</FieldValidation>}
                </Field>
              )}
            />
          )}

          {gateType === PAYMENT_GATE_TYPES.PERCENTAGE && (
            <Controller
              control={control}
              name='minimumPercentage'
              render={({ field, fieldState }) => (
                <Field>
                  <FieldLabel>Minimum percentage of invoice</FieldLabel>
                  <Input
                    type='number'
                    min={1}
                    max={100}
                    inputMode='numeric'
                    value={typeof field.value === 'number' ? String(field.value) : ''}
                    onChange={(event) => {
                      const next = event.target.value;
                      if (next === '') {
                        field.onChange(null);
                        return;
                      }
                      const parsed = Number.parseInt(next, 10);
                      field.onChange(Number.isFinite(parsed) ? parsed : null);
                    }}
                    placeholder='100'
                  />
                  {fieldState.error?.message && <FieldValidation>{fieldState.error.message}</FieldValidation>}
                </Field>
              )}
            />
          )}

          <DialogFooter>
            <Button type='button' variant='ghost' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' variant='default' disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
